import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Pencil, Trash2, CheckCircle, Eye, Banknote, CreditCard, ArrowRightLeft,
  ChevronRight, ClipboardCheck, Clock, Archive, FileDown, FileSignature, Wallet, UserCheck,
  AlertTriangle, CheckCircle2, X, Receipt, Ban, CalendarRange, MoreVertical, LogIn,
} from 'lucide-react'
import {
  getCashClosings, createCashClosing, updateCashClosing,
  receiveCashClosing, deleteCashClosing, voidCashClosing,
  getCashClosingExpenses, addCashClosingExpense, deleteCashClosingExpense, getCashClosingSnapshot,
  recalculateCashClosing,
  type CashClosing, type CashClosingExpense,
} from '@/api/cash-closings'
import { getPayments, type Payment } from '@/api/payments'
import { getUsers, type User } from '@/api/users'
import { listPaymentTypes, type PaymentType } from '@/api/payment-types'
import { buildCashClosingReportPdf, buildHandoverActaPdf, buildMonthlyConsolidationPdf } from '@/lib/payments-pdf'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'

const METHOD_LABELS: Record<string, string> = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia' }

function formatCOP(n: number) { return `$${Number(n).toLocaleString('es-CO')}` }
function formatDate(d: string) { return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) }

function overlaps(aFrom: string, aTo: string, bFrom: string, bTo: string) {
  return aFrom <= bTo && aTo >= bFrom
}

function currentMonth() {
  return new Date().toISOString().slice(0, 7)
}

function monthRange(month: string): [string, string] {
  const [y, m] = month.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return [`${month}-01`, `${month}-${String(lastDay).padStart(2, '0')}`]
}

const EMPTY = { dateFrom: '', dateTo: '', responsible: '', openingCash: '0', notes: '' }
const OPEN_EMPTY = { openedBy: '', openingCash: '0', notes: '' }
const inputCls = 'w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent'
const labelCls = 'block text-xs text-text-muted mb-1'

export function CashClosings({ noHeader = false }: { noHeader?: boolean }) {
  const navigate = useNavigate()
  const [closings, setClosings] = useState<CashClosing[]>([])
  const [payments, setPayments] = useState<Payment[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([])
  const [loading, setLoading] = useState(true)

  // "Abrir turno" dialog (quick open — no date range needed)
  const [shiftOpen, setShiftOpen] = useState(false)
  const [openForm, setOpenForm] = useState(OPEN_EMPTY)
  const [openingSaving, setOpeningSaving] = useState(false)

  // Editar hora de apertura de un turno activo
  const [editOpenedAtShift, setEditOpenedAtShift] = useState<CashClosing | null>(null)
  const [openedAtValue, setOpenedAtValue] = useState('')
  const [savingOpenedAt, setSavingOpenedAt] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [dateConflict, setDateConflict] = useState(false)
  const [saving, setSaving] = useState(false)

  const [pendingDelete, setPendingDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [pendingVoid, setPendingVoid] = useState<string | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [voiding, setVoiding] = useState(false)

  // Detail / reconciliation dialog (covers both draft management and received read-only view)
  const [detail, setDetail] = useState<CashClosing | null>(null)
  const [detailExpenses, setDetailExpenses] = useState<CashClosingExpense[]>([])
  const [loadingExpenses, setLoadingExpenses] = useState(false)
  const [expenseDesc, setExpenseDesc] = useState('')
  const [expenseAmount, setExpenseAmount] = useState('')
  const [addingExpense, setAddingExpense] = useState(false)
  const [countedCashInput, setCountedCashInput] = useState('')
  const [receivedByInput, setReceivedByInput] = useState('')
  const [finalizing, setFinalizing] = useState(false)

  // Consolidado mensual
  const [monthlyOpen, setMonthlyOpen] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(currentMonth())
  const [monthlyExpensesByClosing, setMonthlyExpensesByClosing] = useState<Map<string, number>>(new Map())
  const [loadingMonthlyExpenses, setLoadingMonthlyExpenses] = useState(false)

  useEffect(() => {
    Promise.allSettled([getCashClosings(), getPayments(), getUsers(), listPaymentTypes()])
      .then(([closingsRes, paymentsRes, usersRes, typesRes]) => {
        if (closingsRes.status === 'fulfilled') setClosings(closingsRes.value.closings)
        else toast.error('Error al cargar cierres de caja')
        if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.payments)
        if (usersRes.status === 'fulfilled') setUsers(usersRes.value.users)
        if (typesRes.status === 'fulfilled') setPaymentTypes(typesRes.value.payment_types)
      })
      .finally(() => setLoading(false))
  }, [])

  const userMap = new Map(users.map(u => [u.id, u]))
  const typeMap = new Map(paymentTypes.map(t => [t.id, t.name]))

  function methodLabel(id?: string) {
    return id ? (METHOD_LABELS[id] ?? id) : '—'
  }

  async function handleDownloadReport(closing: CashClosing) {
    const from = closing.date_from.slice(0, 10)
    const to = closing.date_to.slice(0, 10)
    const closingPayments = payments.filter(p => {
      const d = p.paid_at.slice(0, 10)
      return d >= from && d <= to
    })
    const breakdownMap = new Map<string, { name: string; count: number; total: number }>()
    for (const p of closingPayments) {
      const key = p.payment_type_id ?? (p.membership_id ? '__plan__' + (p.plan_name ?? '') : '__none__')
      const name = p.payment_type_id ? (typeMap.get(p.payment_type_id) ?? 'Sin tipo') : p.membership_id ? (p.plan_name ?? 'Membresía') : 'Sin tipo'
      const entry = breakdownMap.get(key) ?? { name, count: 0, total: 0 }
      entry.count += 1
      entry.total += Number(p.amount)
      breakdownMap.set(key, entry)
    }
    const typeBreakdown = [...breakdownMap.values()].sort((a, b) => b.total - a.total)
    let expenses: { description: string; amount: number }[] = detail?.id === closing.id ? detailExpenses : []
    if (detail?.id !== closing.id) {
      try {
        const r = await getCashClosingExpenses(closing.id)
        expenses = r.expenses
      } catch {
        expenses = []
      }
    }
    buildCashClosingReportPdf({ closing, payments: closingPayments, userMap, typeBreakdown, methodLabel, expenses })
    toast.success('Reporte generado')
  }

  async function handleDownloadActa(closing: CashClosing) {
    try {
      const r = await getCashClosingSnapshot(closing.id)
      buildHandoverActaPdf({ closing, snapshot: r.snapshot })
      toast.success('Acta de entrega generada')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Este cierre todavía no tiene un acta de entrega')
    }
  }

  function openCreate() {
    setEditingId(null)
    setForm(EMPTY)
    setDateConflict(false)
    setFormOpen(true)
  }

  function openEdit(c: CashClosing) {
    setEditingId(c.id)
    setForm({ dateFrom: c.date_from.slice(0, 10), dateTo: c.date_to.slice(0, 10), responsible: c.responsible, openingCash: String(c.opening_cash ?? 0), notes: c.notes ?? '' })
    setDateConflict(false)
    setFormOpen(true)
  }

  function checkConflict(dateFrom: string, dateTo: string) {
    if (!dateFrom || !dateTo || dateTo < dateFrom) { setDateConflict(false); return }
    setDateConflict(closings.some(c => editingId !== c.id && c.status !== 'voided' && overlaps(dateFrom, dateTo, c.date_from.slice(0, 10), c.date_to.slice(0, 10))))
  }

  function handleChange(field: keyof typeof EMPTY, value: string) {
    const next = { ...form, [field]: value }
    setForm(next)
    if (field === 'dateFrom' || field === 'dateTo') {
      checkConflict(field === 'dateFrom' ? value : form.dateFrom, field === 'dateTo' ? value : form.dateTo)
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (dateConflict) { toast.error('Las fechas se cruzan con otro cierre existente'); return }
    setSaving(true)
    try {
      const openingCash = Number(form.openingCash) || 0
      if (editingId) {
        const r = await updateCashClosing(editingId, { dateFrom: form.dateFrom, dateTo: form.dateTo, responsible: form.responsible, openingCash, notes: form.notes || undefined })
        setClosings(prev => prev.map(c => c.id === editingId ? { ...c, ...r.closing } : c))
        toast.success('Cierre actualizado')
      } else {
        const r = await createCashClosing({ openedBy: form.responsible, dateFrom: form.dateFrom, dateTo: form.dateTo, responsible: form.responsible, openingCash, notes: form.notes || undefined })
        setClosings(prev => [r.closing, ...prev])
        toast.success('Cierre creado')
      }
      setFormOpen(false)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  async function handleOpenShift(e: React.FormEvent) {
    e.preventDefault()
    if (!openForm.openedBy.trim()) { toast.error('Indica el nombre del cajero'); return }
    setOpeningSaving(true)
    try {
      const r = await createCashClosing({
        openedBy: openForm.openedBy.trim(),
        openingCash: Number(openForm.openingCash) || 0,
        notes: openForm.notes || undefined,
      })
      setClosings(prev => [r.closing, ...prev])
      setShiftOpen(false)
      setOpenForm(OPEN_EMPTY)
      toast.success(`Caja abierta por ${openForm.openedBy.trim()}`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al abrir caja')
    } finally {
      setOpeningSaving(false)
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await deleteCashClosing(pendingDelete)
      setClosings(prev => prev.filter(c => c.id !== pendingDelete))
      setPendingDelete(null)
      toast.success('Cierre eliminado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error')
    } finally {
      setDeleting(false)
    }
  }

  async function handleVoid() {
    if (!pendingVoid) return
    setVoiding(true)
    try {
      const r = await voidCashClosing(pendingVoid, voidReason.trim() || undefined)
      setClosings(prev => prev.map(c => c.id === pendingVoid ? { ...c, ...r.closing } : c))
      if (detail?.id === pendingVoid) setDetail(prev => prev ? { ...prev, ...r.closing } : prev)
      setPendingVoid(null)
      setVoidReason('')
      toast.success('Cierre anulado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al anular')
    } finally {
      setVoiding(false)
    }
  }

  function openDetail(c: CashClosing) {
    setDetail(c)
    setCountedCashInput(c.counted_cash != null ? String(c.counted_cash) : '')
    setReceivedByInput(c.received_by ?? '')
    setExpenseDesc('')
    setExpenseAmount('')
    setLoadingExpenses(true)
    getCashClosingExpenses(c.id)
      .then(r => setDetailExpenses(r.expenses))
      .catch(() => toast.error('Error al cargar gastos de caja'))
      .finally(() => setLoadingExpenses(false))
    if (c.status === 'draft') {
      recalculateCashClosing(c.id)
        .then(r => {
          setDetail(prev => prev && prev.id === c.id ? { ...prev, ...r.closing } : prev)
          setClosings(prev => prev.map(cc => cc.id === c.id ? { ...cc, ...r.closing } : cc))
        })
        .catch(() => {})
    }
  }

  async function handleAddExpense() {
    if (!detail) return
    if (!expenseDesc.trim() || !expenseAmount) { toast.error('Descripción y monto son requeridos'); return }
    setAddingExpense(true)
    try {
      const r = await addCashClosingExpense(detail.id, { description: expenseDesc.trim(), amount: Number(expenseAmount) })
      setDetailExpenses(prev => [...prev, r.expense])
      setExpenseDesc('')
      setExpenseAmount('')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al agregar gasto')
    } finally {
      setAddingExpense(false)
    }
  }

  async function handleDeleteExpense(expenseId: string) {
    if (!detail) return
    try {
      await deleteCashClosingExpense(detail.id, expenseId)
      setDetailExpenses(prev => prev.filter(e => e.id !== expenseId))
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar gasto')
    }
  }

  const totalExpenses = detailExpenses.reduce((s, e) => s + Number(e.amount), 0)
  const expectedCash = detail ? Number(detail.opening_cash || 0) + Number(detail.total_cash) - totalExpenses : 0
  const countedNum = countedCashInput !== '' ? Number(countedCashInput) : null
  const difference = countedNum !== null ? countedNum - expectedCash : null

  async function handleFinalize() {
    if (!detail) return
    if (!receivedByInput.trim()) { toast.error('Indica quién recibe la caja'); return }
    setFinalizing(true)
    try {
      const r = await receiveCashClosing(detail.id, {
        receivedBy: receivedByInput.trim(),
        countedCash: countedCashInput !== '' ? Number(countedCashInput) : undefined,
      })
      setClosings(prev => prev.map(c => c.id === detail.id ? { ...r.closing, total_expenses: totalExpenses } : c))
      setDetail(null)
      toast.success('Caja marcada como recibida')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error')
    } finally {
      setFinalizing(false)
    }
  }

  const totalDraft    = closings.filter(c => c.status === 'draft').reduce((s, c) => s + Number(c.grand_total), 0)
  const totalReceived = closings.filter(c => c.status === 'received').reduce((s, c) => s + Number(c.grand_total), 0)
  const draftCount    = closings.filter(c => c.status === 'draft').length
  const receivedCount = closings.filter(c => c.status === 'received').length

  const todayStr      = new Date().toISOString().split('T')[0]
  const todayPayments = payments.filter(p => p.paid_at.slice(0, 10) === todayStr)

  // Active shift = any draft for today
  const activeShifts = closings.filter(c => c.status === 'draft' && c.date_from.slice(0, 10) === todayStr)

  function formatTime(ts?: string) {
    if (!ts) return ''
    return new Date(ts).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
  }
  // Local calendar date/time for a datetime-local input — never toISOString()
  // here, it converts to UTC and the input treats the bare result as if it
  // were already local, silently shifting the displayed time by hours.
  function toLocalInputValue(ts: string) {
    const d = new Date(ts)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  function openEditOpenedAt(shift: CashClosing) {
    setEditOpenedAtShift(shift)
    setOpenedAtValue(shift.opened_at ? toLocalInputValue(shift.opened_at) : '')
  }

  async function handleSaveOpenedAt(e: React.FormEvent) {
    e.preventDefault()
    if (!editOpenedAtShift || !openedAtValue) return
    setSavingOpenedAt(true)
    try {
      const r = await updateCashClosing(editOpenedAtShift.id, { openedAt: `${openedAtValue}:00` })
      setClosings(prev => prev.map(c => c.id === r.closing.id ? { ...c, ...r.closing } : c))
      toast.success('Hora de apertura actualizada')
      setEditOpenedAtShift(null)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al actualizar la hora de apertura')
    } finally {
      setSavingOpenedAt(false)
    }
  }
  const todayTotal    = todayPayments.reduce((s, p) => s + Number(p.amount), 0)
  const todayCash     = todayPayments.filter(p => p.payment_method_id === 'cash').reduce((s, p) => s + Number(p.amount), 0)
  const todayCard     = todayPayments.filter(p => p.payment_method_id === 'card').reduce((s, p) => s + Number(p.amount), 0)
  const todayTransfer = todayPayments.filter(p => p.payment_method_id === 'transfer').reduce((s, p) => s + Number(p.amount), 0)

  // ── Consolidado mensual ──
  const [monthStart, monthEnd] = monthRange(selectedMonth)
  const monthPayments = payments.filter(p => p.paid_at.slice(0, 10) >= monthStart && p.paid_at.slice(0, 10) <= monthEnd)
  const monthClosings = closings.filter(c => overlaps(monthStart, monthEnd, c.date_from.slice(0, 10), c.date_to.slice(0, 10)))
  const reconciledMonthClosings = monthClosings.filter(c => c.status !== 'voided' && c.counted_cash != null)

  useEffect(() => {
    if (!monthlyOpen) return
    const toFetch = reconciledMonthClosings.filter(c => !monthlyExpensesByClosing.has(c.id))
    if (toFetch.length === 0) return
    setLoadingMonthlyExpenses(true)
    Promise.all(
      toFetch.map(c =>
        getCashClosingExpenses(c.id)
          .then(r => [c.id, r.expenses.reduce((s, e) => s + Number(e.amount), 0)] as [string, number])
          .catch(() => [c.id, 0] as [string, number])
      )
    )
      .then(results => {
        setMonthlyExpensesByClosing(prev => {
          const next = new Map(prev)
          for (const [id, total] of results) next.set(id, total)
          return next
        })
      })
      .finally(() => setLoadingMonthlyExpenses(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyOpen, selectedMonth, closings])

  const methodTotals = monthPayments.reduce(
    (acc, p) => {
      const amt = Number(p.amount)
      if (p.payment_method_id === 'cash') acc.cash += amt
      else if (p.payment_method_id === 'card') acc.card += amt
      else if (p.payment_method_id === 'transfer') acc.transfer += amt
      else acc.other += amt
      return acc
    },
    { cash: 0, card: 0, transfer: 0, other: 0 }
  )

  const monthlyBreakdownMap = new Map<string, { name: string; count: number; total: number }>()
  for (const p of monthPayments) {
    const key = p.payment_type_id ?? (p.membership_id ? '__plan__' + (p.plan_name ?? '') : '__none__')
    const name = p.payment_type_id ? (typeMap.get(p.payment_type_id) ?? 'Sin tipo') : p.membership_id ? (p.plan_name ?? 'Membresía') : 'Sin tipo'
    const entry = monthlyBreakdownMap.get(key) ?? { name, count: 0, total: 0 }
    entry.count += 1
    entry.total += Number(p.amount)
    monthlyBreakdownMap.set(key, entry)
  }
  const monthlyTypeBreakdown = [...monthlyBreakdownMap.values()].sort((a, b) => b.total - a.total)

  const monthlyReconciliation = reconciledMonthClosings.reduce(
    (acc, c) => {
      const exp = monthlyExpensesByClosing.get(c.id) ?? 0
      const expected = Number(c.opening_cash || 0) + Number(c.total_cash) - exp
      acc.totalOpening += Number(c.opening_cash || 0)
      acc.totalExpenses += exp
      acc.totalExpected += expected
      acc.totalCounted += Number(c.counted_cash)
      acc.reconciledCount += 1
      return acc
    },
    { totalOpening: 0, totalExpenses: 0, totalExpected: 0, totalCounted: 0, reconciledCount: 0 }
  )
  const monthlyDifference = monthlyReconciliation.totalCounted - monthlyReconciliation.totalExpected

  function handleDownloadMonthlyPdf() {
    buildMonthlyConsolidationPdf({
      month: selectedMonth,
      payments: monthPayments,
      userMap,
      typeBreakdown: monthlyTypeBreakdown,
      methodLabel,
      methodTotals,
      closings: monthClosings,
      reconciliation: { ...monthlyReconciliation, totalDifference: monthlyDifference },
    })
    toast.success('PDF generado')
  }

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ── */}
      {noHeader ? (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setMonthlyOpen(true)}
            className="flex items-center gap-2 bg-surface border border-border text-text-primary text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-background transition-colors cursor-pointer"
          >
            <CalendarRange className="w-4 h-4" />
            Consolidado mensual
          </button>
          <button
            onClick={() => { setShiftOpen(true); setOpenForm(OPEN_EMPTY) }}
            className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20"
          >
            <LogIn className="w-4 h-4" />
            Abrir Caja
          </button>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-text-muted mb-1">
              <button onClick={() => navigate('/payments')} className="hover:text-accent transition-colors cursor-pointer font-medium">
                Caja
              </button>
              <ChevronRight className="w-3 h-3" />
              <span className="text-text-primary font-medium">Cierres</span>
            </div>
            <h1 className="text-text-primary font-bold text-xl leading-tight">Cierres de Caja</h1>
            <p className="text-text-muted text-xs mt-1">{closings.length} registros en total</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setMonthlyOpen(true)}
              className="flex items-center gap-2 bg-surface border border-border text-text-primary text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-background transition-colors cursor-pointer"
            >
              <CalendarRange className="w-4 h-4" />
              Consolidado mensual
            </button>
            <button
              onClick={() => { setShiftOpen(true); setOpenForm(OPEN_EMPTY) }}
              className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20"
            >
              <LogIn className="w-4 h-4" />
              Abrir Caja
            </button>
          </div>
        </div>
      )}

      {/* ── Active shift banner ── */}
      {activeShifts.length > 0 && (
        <div className="flex flex-col gap-2">
          {activeShifts.map(shift => (
            <div
              key={shift.id}
              className="flex items-center justify-between gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-xl px-4 py-3 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-950/50 transition-colors"
              onClick={() => openDetail(shift)}
            >
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                <div>
                  <p className="text-emerald-800 dark:text-emerald-300 text-sm font-semibold">
                    Caja abierta — {shift.opened_by ?? shift.responsible}
                  </p>
                  <p className="text-emerald-700 dark:text-emerald-400 text-xs mt-0.5 flex items-center gap-1.5">
                    Desde {shift.opened_at ? formatTime(shift.opened_at) : 'hoy'} · {formatCOP(Number(shift.grand_total))} acumulado
                    <button
                      onClick={e => { e.stopPropagation(); openEditOpenedAt(shift) }}
                      aria-label="Editar hora de apertura"
                      title="Editar hora de apertura"
                      className="text-emerald-600/70 dark:text-emerald-500/70 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors cursor-pointer"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </p>
                </div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); openDetail(shift) }}
                className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 px-3 py-1.5 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors cursor-pointer"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Cerrar turno
              </button>
            </div>
          ))}
        </div>
      )}
      {activeShifts.length === 0 && !loading && (
        <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl px-4 py-3">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
          <p className="text-amber-800 dark:text-amber-300 text-sm">
            <span className="font-semibold">Sin caja abierta hoy.</span>{' '}
            <button
              onClick={() => { setShiftOpen(true); setOpenForm(OPEN_EMPTY) }}
              className="underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 transition-colors cursor-pointer"
            >
              Abrir turno
            </button>
          </p>
        </div>
      )}

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">

        {/* Hoy */}
        <div className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-3 col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-accent" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Hoy</span>
          </div>
          <div>
            <p className="text-text-primary text-2xl font-black tabular-nums leading-none">{formatCOP(todayTotal)}</p>
            <p className="text-text-muted text-[11px] mt-1">{todayPayments.length} cobro{todayPayments.length !== 1 ? 's' : ''} registrados</p>
          </div>
          {todayTotal > 0 && (
            <div className="flex gap-3 pt-2 border-t border-border">
              {todayCash > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[9px] text-text-muted font-semibold uppercase tracking-wide">Efectivo</span>
                  <span className="text-text-primary text-xs font-bold tabular-nums">{formatCOP(todayCash)}</span>
                </div>
              )}
              {todayCard > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[9px] text-text-muted font-semibold uppercase tracking-wide">Tarjeta</span>
                  <span className="text-text-primary text-xs font-bold tabular-nums">{formatCOP(todayCard)}</span>
                </div>
              )}
              {todayTransfer > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[9px] text-text-muted font-semibold uppercase tracking-wide">Transfer.</span>
                  <span className="text-text-primary text-xs font-bold tabular-nums">{formatCOP(todayTransfer)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Borradores */}
        <div className={`bg-surface border rounded-2xl p-5 flex flex-col gap-3 ${draftCount > 0 ? 'border-amber-500/25' : 'border-border'}`}>
          <div className="flex items-center justify-between">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${draftCount > 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-background border border-border'}`}>
              <Clock className={`w-4 h-4 ${draftCount > 0 ? 'text-amber-500' : 'text-text-muted'}`} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Borrador</span>
          </div>
          <div>
            <p className={`text-2xl font-black tabular-nums leading-none ${draftCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-text-primary'}`}>
              {formatCOP(totalDraft)}
            </p>
            <p className="text-text-muted text-[11px] mt-1">
              {draftCount === 0 ? 'Sin borradores pendientes' : `${draftCount} cierre${draftCount !== 1 ? 's' : ''} por cerrar`}
            </p>
          </div>
          {draftCount > 0 && (
            <div className="flex items-center gap-1.5 pt-2 border-t border-amber-500/15">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-amber-600 dark:text-amber-400 text-[10px] font-semibold">Pendiente de recibir</span>
            </div>
          )}
        </div>

        {/* Recibidos */}
        <div className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-emerald-500" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Recibidos</span>
          </div>
          <div>
            <p className="text-emerald-600 dark:text-emerald-400 text-2xl font-black tabular-nums leading-none">{formatCOP(totalReceived)}</p>
            <p className="text-text-muted text-[11px] mt-1">{receivedCount} cierre{receivedCount !== 1 ? 's' : ''} confirmados</p>
          </div>
          <div className="flex items-center gap-1.5 pt-2 border-t border-border">
            <ClipboardCheck className="w-3 h-3 text-emerald-500" />
            <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-semibold">Total histórico</span>
          </div>
        </div>

        {/* Total cierres */}
        <div className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <Archive className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Historial</span>
          </div>
          <div>
            <p className="text-blue-600 dark:text-blue-400 text-2xl font-black tabular-nums leading-none">{closings.length}</p>
            <p className="text-text-muted text-[11px] mt-1">cierre{closings.length !== 1 ? 's' : ''} en total</p>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-border">
            {receivedCount > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[10px] text-text-muted">{receivedCount} recibidos</span>
              </div>
            )}
            {draftCount > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span className="text-[10px] text-text-muted">{draftCount} borrador</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabla ── */}
      {loading ? (
        <p className="text-text-muted text-sm">Cargando...</p>
      ) : (
        <div className="bg-surface rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">N°</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Período</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Cajero</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Estado</th>
                <th className="text-right px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-text-muted text-xs font-bold uppercase tracking-wide">
                    <Banknote className="w-3 h-3" /> Efectivo
                  </span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-text-muted text-xs font-bold uppercase tracking-wide">
                    <CreditCard className="w-3 h-3" /> Tarjeta
                  </span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-text-muted text-xs font-bold uppercase tracking-wide">
                    <ArrowRightLeft className="w-3 h-3" /> Transfer.
                  </span>
                </th>
                <th className="text-right px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Total</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {closings.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-2 text-text-muted">
                      <Archive className="w-6 h-6 opacity-40" />
                      <p className="text-xs">Sin cierres registrados. Crea el primero con "Nuevo cierre".</p>
                    </div>
                  </td>
                </tr>
              )}
              {closings.map(c => (
                <tr key={c.id} className={`border-b border-border last:border-0 hover:bg-background/30 transition-colors group ${c.status === 'voided' ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-text-muted text-xs font-mono tabular-nums">#{c.closing_number}</td>
                  <td className="px-4 py-3">
                    {c.date_from.slice(0, 10) === c.date_to.slice(0, 10) ? (
                      <p className="text-text-primary text-xs font-semibold">{formatDate(c.date_from)}</p>
                    ) : (
                      <p className="text-text-primary text-xs font-semibold">{formatDate(c.date_from)} → {formatDate(c.date_to)}</p>
                    )}
                    {c.opened_at && (
                      <p className="text-text-muted text-[10px] mt-0.5">
                        Apertura {formatTime(c.opened_at)}{c.received_at ? ` · Cierre ${formatTime(c.received_at)}` : ''}
                      </p>
                    )}
                    <p className="text-text-muted text-[10px] mt-0.5">{c.payment_count} cobro{c.payment_count !== 1 ? 's' : ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-text-primary text-xs">{c.opened_by ?? c.responsible}</p>
                    {c.received_by && <p className="text-text-muted text-[10px] mt-0.5">Recibido: {c.received_by}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold border ${
                      c.status === 'received'
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                        : c.status === 'voided'
                        ? 'bg-slate-100 dark:bg-slate-800/40 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                        : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400'
                    }`}>
                      {c.status === 'received'
                        ? <><CheckCircle className="w-3 h-3" /> Recibida</>
                        : c.status === 'voided'
                        ? <><Ban className="w-3 h-3" /> Anulada</>
                        : <><Clock className="w-3 h-3" /> Borrador</>
                      }
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-text-primary text-xs tabular-nums">{Number(c.total_cash)     > 0 ? formatCOP(Number(c.total_cash))     : '—'}</td>
                  <td className="px-4 py-3 text-right text-text-primary text-xs tabular-nums">{Number(c.total_card)     > 0 ? formatCOP(Number(c.total_card))     : '—'}</td>
                  <td className="px-4 py-3 text-right text-text-primary text-xs tabular-nums">{Number(c.total_transfer) > 0 ? formatCOP(Number(c.total_transfer)) : '—'}</td>
                  <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400 text-xs font-bold tabular-nums">{formatCOP(Number(c.grand_total))}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openDetail(c)} className="text-text-muted hover:text-accent transition-colors cursor-pointer p-1.5 rounded-md hover:bg-background" title="Ver detalle">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-1.5 rounded-md hover:bg-background" title="Más acciones">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-surface border-border text-text-primary">
                          <DropdownMenuItem onClick={() => handleDownloadReport(c)} className="cursor-pointer focus:bg-background focus:text-text-primary">
                            <FileDown className="w-3.5 h-3.5" /> Reporte de cierre
                          </DropdownMenuItem>
                          {c.status !== 'draft' && (
                            <DropdownMenuItem onClick={() => handleDownloadActa(c)} className="cursor-pointer focus:bg-background focus:text-text-primary">
                              <FileSignature className="w-3.5 h-3.5" /> Acta de entrega
                            </DropdownMenuItem>
                          )}
                          {c.status === 'draft' && (
                            <>
                              <DropdownMenuItem onClick={() => openEdit(c)} className="cursor-pointer focus:bg-background focus:text-text-primary">
                                <Pencil className="w-3.5 h-3.5" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-border" />
                              <DropdownMenuItem onClick={() => setPendingDelete(c.id)} className="cursor-pointer text-red-500 focus:bg-red-50 dark:focus:bg-red-950/30 focus:text-red-500">
                                <Trash2 className="w-3.5 h-3.5" /> Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Abrir turno dialog ── */}
      <Dialog open={shiftOpen} onOpenChange={o => !o && setShiftOpen(false)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="w-4 h-4 text-accent" />
              Abrir Caja
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOpenShift} className="flex flex-col gap-4 mt-2">
            <div>
              <label className={labelCls}>Cajero *</label>
              <input
                type="text" required autoFocus maxLength={200}
                className={inputCls}
                value={openForm.openedBy}
                onChange={e => setOpenForm(prev => ({ ...prev, openedBy: e.target.value }))}
                placeholder="Nombre de quien abre la caja"
              />
            </div>
            <div>
              <label className={labelCls}>Fondo inicial de caja</label>
              <div className="flex items-center bg-background border border-border rounded-md px-3 py-2 gap-2">
                <Wallet className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <input
                  type="number" min="0" step="0.01"
                  className="flex-1 bg-transparent text-sm text-text-primary focus:outline-none"
                  value={openForm.openingCash}
                  onChange={e => setOpenForm(prev => ({ ...prev, openingCash: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <p className="text-text-muted text-[11px] mt-1">Efectivo físico con el que inicia el turno.</p>
            </div>
            <div>
              <label className={labelCls}>Notas</label>
              <textarea
                className={inputCls} rows={2} maxLength={500}
                value={openForm.notes}
                onChange={e => setOpenForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
            <p className="text-text-muted text-[11px] bg-background/50 border border-border rounded-md px-3 py-2">
              La caja se abre para hoy. Los cobros del turno se acumulan automáticamente.
            </p>
            <DialogFooter>
              <button type="button" onClick={() => setShiftOpen(false)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancelar</button>
              <button type="submit" disabled={openingSaving} className="px-4 py-2 bg-accent text-white text-sm font-semibold rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
                {openingSaving ? 'Abriendo...' : 'Abrir Caja'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Editar hora de apertura de un turno activo */}
      <Dialog open={!!editOpenedAtShift} onOpenChange={o => !o && setEditOpenedAtShift(null)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-accent" />
              Hora de apertura
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveOpenedAt} className="flex flex-col gap-4 mt-2">
            <div>
              <label className={labelCls}>Caja abierta desde</label>
              <input
                type="datetime-local" required autoFocus
                className={inputCls}
                value={openedAtValue}
                onChange={e => setOpenedAtValue(e.target.value)}
                max={toLocalInputValue(new Date().toISOString())}
              />
            </div>
            <p className="text-text-muted text-[11px] bg-background/50 border border-border rounded-md px-3 py-2">
              Los totales de este turno se recalculan con la hora nueva — solo se cuentan los cobros hechos desde ese momento.
            </p>
            <DialogFooter>
              <button type="button" onClick={() => setEditOpenedAtShift(null)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancelar</button>
              <button type="submit" disabled={savingOpenedAt} className="px-4 py-2 bg-accent text-white text-sm font-semibold rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
                {savingOpenedAt ? 'Guardando...' : 'Guardar'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={o => !o && setFormOpen(false)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-md">
          <DialogHeader><DialogTitle>{editingId ? 'Editar cierre' : 'Nuevo cierre de caja'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="flex flex-col gap-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Fecha desde *</label>
                <input
                  type="date" required
                  className={`${inputCls} ${dateConflict ? 'border-red-500 focus:ring-red-500' : ''}`}
                  value={form.dateFrom}
                  onChange={e => handleChange('dateFrom', e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Fecha hasta *</label>
                <input
                  type="date" required
                  min={form.dateFrom}
                  className={`${inputCls} ${dateConflict ? 'border-red-500 focus:ring-red-500' : ''}`}
                  value={form.dateTo}
                  onChange={e => handleChange('dateTo', e.target.value)}
                />
              </div>
            </div>
            {dateConflict && (
              <p className="text-red-600 dark:text-red-400 text-xs -mt-2">Estas fechas se cruzan con un cierre de caja existente.</p>
            )}
            <div>
              <label className={labelCls}>Cajero *</label>
              <input
                type="text" required maxLength={200}
                className={inputCls}
                value={form.responsible}
                onChange={e => handleChange('responsible', e.target.value)}
                placeholder="Nombre de quien tiene la caja en este turno"
              />
            </div>
            <div>
              <label className={labelCls}>Fondo inicial de caja</label>
              <div className="flex items-center bg-background border border-border rounded-md px-3 py-2 gap-2">
                <Wallet className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <input
                  type="number" min="0" step="0.01"
                  className="flex-1 bg-transparent text-sm text-text-primary focus:outline-none"
                  value={form.openingCash}
                  onChange={e => handleChange('openingCash', e.target.value)}
                  placeholder="0"
                />
              </div>
              <p className="text-text-muted text-[11px] mt-1">Efectivo con el que se abrió la caja al inicio del turno.</p>
            </div>
            <div>
              <label className={labelCls}>Notas</label>
              <textarea
                className={inputCls} rows={2} maxLength={500}
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                placeholder="Opcional"
              />
            </div>
            <p className="text-text-muted text-xs bg-background/50 border border-border rounded-md px-3 py-2">
              Los totales por método de pago se calculan automáticamente a partir de los cobros registrados en el período seleccionado.
            </p>
            <DialogFooter>
              <button type="button" onClick={() => setFormOpen(false)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors">Cancelar</button>
              <button type="submit" disabled={saving || dateConflict} className="px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail / reconciliation dialog — draft management + received read-only view */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        {detail && (
          <DialogContent className="bg-surface border-border text-text-primary max-w-2xl">
            <DialogHeader>
              <DialogTitle>Cierre de Caja N° {detail.closing_number}</DialogTitle>
              <p className="text-text-muted text-xs mt-0.5">{formatDate(detail.date_from)} → {formatDate(detail.date_to)}</p>
            </DialogHeader>
            <div className="mt-2 max-h-[75vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-x-5 gap-y-3">

                {/* ── Columna izquierda: info del turno + desglose de ingresos ── */}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Cajero</span>
                      <span className="text-text-primary font-medium">{detail.opened_by ?? detail.responsible}</span>
                    </div>
                    {detail.opened_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Apertura</span>
                        <span className="text-text-primary">{new Date(detail.opened_at).toLocaleString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Cobros</span>
                      <span className="text-text-primary">{detail.payment_count}</span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-3 flex flex-col gap-2">
                    <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5" /> Efectivo</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(Number(detail.total_cash))}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Tarjeta</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(Number(detail.total_card))}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5" /> Transferencia</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(Number(detail.total_transfer))}</span></div>
                    {Number(detail.total_other) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-text-muted">Otros</span><span className="text-text-primary font-medium">{formatCOP(Number(detail.total_other))}</span></div>
                    )}
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between items-center">
                    <span className="text-text-muted text-xs font-bold uppercase">Total ventas</span>
                    <span className="text-emerald-600 dark:text-emerald-400 text-xl font-bold">{formatCOP(Number(detail.grand_total))}</span>
                  </div>

                  {detail.notes && <p className="text-text-muted text-xs italic border-t border-border pt-2">{detail.notes}</p>}
                  {detail.received_at && (
                    <p className="text-text-muted text-[10px] border-t border-border pt-2">
                      Recibida el {new Date(detail.received_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>

                {/* ── Columna derecha: reconciliación de efectivo ── */}
                <div className="flex flex-col gap-2.5">
                  <h3 className="text-text-primary text-xs font-bold uppercase tracking-wide flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" /> Reconciliación de efectivo
                  </h3>

                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Fondo inicial</span>
                    <span className="text-text-primary font-medium tabular-nums">{formatCOP(Number(detail.opening_cash || 0))}</span>
                  </div>

                  {/* Expenses list */}
                  <div className="bg-background/50 border border-border rounded-lg px-3 py-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-text-muted text-xs font-semibold flex items-center gap-1.5"><Receipt className="w-3 h-3" /> Gastos de caja</span>
                      <span className="text-amber-600 dark:text-amber-400 text-xs font-bold">-{formatCOP(totalExpenses)}</span>
                    </div>
                    {loadingExpenses ? (
                      <p className="text-text-muted text-xs">Cargando...</p>
                    ) : detailExpenses.length === 0 ? (
                      <p className="text-text-muted text-xs">Sin gastos registrados</p>
                    ) : (
                      <div className="flex flex-col gap-1 mb-1">
                        {detailExpenses.map(e => (
                          <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-text-primary truncate">{e.description}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-text-muted tabular-nums">{formatCOP(Number(e.amount))}</span>
                              {detail.status === 'draft' && (
                                <button onClick={() => handleDeleteExpense(e.id)} className="text-text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer" title="Eliminar gasto">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {detail.status === 'draft' && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <input
                          type="text" placeholder="Descripción del gasto"
                          className="flex-1 bg-background border border-border rounded-md px-2 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                          value={expenseDesc}
                          onChange={e => setExpenseDesc(e.target.value)}
                        />
                        <input
                          type="number" min="0" step="0.01" placeholder="Monto"
                          className="w-24 bg-background border border-border rounded-md px-2 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                          value={expenseAmount}
                          onChange={e => setExpenseAmount(e.target.value)}
                        />
                        <button
                          onClick={handleAddExpense}
                          disabled={addingExpense}
                          className="shrink-0 bg-accent/10 border border-accent/30 text-accent rounded-md px-2 py-1.5 hover:bg-accent/20 transition-colors cursor-pointer disabled:opacity-50"
                          title="Agregar gasto"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Efectivo esperado</span>
                    <span className="text-text-primary font-bold tabular-nums">{formatCOP(expectedCash)}</span>
                  </div>

                  {detail.status === 'draft' ? (
                    <>
                      <div>
                        <label className={labelCls}>Efectivo contado *</label>
                        <input
                          type="number" min="0" step="0.01"
                          className={inputCls}
                          value={countedCashInput}
                          onChange={e => setCountedCashInput(e.target.value)}
                          placeholder="Cantidad física contada en caja"
                        />
                      </div>
                      {difference !== null && (
                        <div className={`flex items-center justify-between text-xs font-semibold px-3 py-2 rounded-lg border ${
                          difference === 0
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                            : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400'
                        }`}>
                          <span className="flex items-center gap-1.5">
                            {difference === 0 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                            {difference === 0 ? 'Caja exacta' : difference > 0 ? 'Sobrante' : 'Faltante'}
                          </span>
                          <span>{difference !== 0 ? formatCOP(Math.abs(difference)) : formatCOP(0)}</span>
                        </div>
                      )}
                      <div>
                        <label className={labelCls}>Administrador que recibe *</label>
                        <div className="flex items-center bg-background border border-border rounded-md px-3 py-2 gap-2">
                          <UserCheck className="w-3.5 h-3.5 text-text-muted shrink-0" />
                          <input
                            type="text" maxLength={200}
                            className="flex-1 bg-transparent text-sm text-text-primary focus:outline-none"
                            value={receivedByInput}
                            onChange={e => setReceivedByInput(e.target.value)}
                            placeholder="Nombre del administrador que recibe la caja"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Efectivo contado</span>
                        <span className="text-text-primary font-medium tabular-nums">{detail.counted_cash != null ? formatCOP(Number(detail.counted_cash)) : '—'}</span>
                      </div>
                      {detail.counted_cash != null && (
                        <div className={`flex items-center justify-between text-xs font-semibold px-3 py-2 rounded-lg border ${
                          Number(detail.counted_cash) - expectedCash === 0
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                            : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400'
                        }`}>
                          <span className="flex items-center gap-1.5">
                            {Number(detail.counted_cash) - expectedCash === 0 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                            {Number(detail.counted_cash) - expectedCash === 0 ? 'Caja exacta' : Number(detail.counted_cash) - expectedCash > 0 ? 'Sobrante' : 'Faltante'}
                          </span>
                          <span>{formatCOP(Math.abs(Number(detail.counted_cash) - expectedCash))}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Administrador que recibe</span>
                        <span className="text-text-primary font-medium">{detail.received_by ?? '—'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ── Fila completa: anulado ── */}
              {detail.status === 'voided' && (
                <div className="mt-3 bg-slate-100 dark:bg-slate-800/40 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2.5 flex flex-col gap-1">
                  <span className="text-slate-600 dark:text-slate-400 text-xs font-semibold flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Cierre anulado</span>
                  {detail.void_reason && <span className="text-slate-600 dark:text-slate-400 text-xs">{detail.void_reason}</span>}
                  {detail.voided_at && (
                    <span className="text-slate-500 text-[10px]">
                      {new Date(detail.voided_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="mt-4 flex gap-2">
              <button onClick={() => handleDownloadReport(detail)} className="flex items-center justify-center gap-2 px-3 py-2 border border-border text-text-primary text-sm rounded-md hover:bg-background transition-colors cursor-pointer" title="Reporte de cierre">
                <FileDown className="w-4 h-4" />
              </button>
              {detail.status !== 'draft' && (
                <button onClick={() => handleDownloadActa(detail)} className="flex items-center justify-center gap-2 px-3 py-2 border border-border text-text-primary text-sm rounded-md hover:bg-background transition-colors cursor-pointer" title="Acta de entrega">
                  <FileSignature className="w-4 h-4" />
                </button>
              )}
              {detail.status === 'draft' ? (
                <button onClick={handleFinalize} disabled={finalizing} className="flex-1 px-4 py-2 bg-accent text-white text-sm font-semibold rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
                  {finalizing ? 'Procesando...' : 'Confirmar y marcar como recibida'}
                </button>
              ) : detail.status === 'received' ? (
                <>
                  <button onClick={() => setPendingVoid(detail.id)} className="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-900/50 text-red-600 dark:text-red-400 text-sm rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer" title="Anular este cierre">
                    <Ban className="w-4 h-4" /> Anular
                  </button>
                  <button onClick={() => setDetail(null)} className="flex-1 px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent/90 transition-colors">Cerrar</button>
                </>
              ) : (
                <button onClick={() => setDetail(null)} className="flex-1 px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent/90 transition-colors">Cerrar</button>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Consolidado mensual */}
      <Dialog open={monthlyOpen} onOpenChange={setMonthlyOpen}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-md">
          <DialogHeader><DialogTitle>Consolidado mensual</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 mt-2 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <label className={labelCls}>Mes</label>
              <input
                type="month"
                className={inputCls}
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
              />
            </div>

            <div className="border-t border-border pt-3 flex justify-between items-center">
              <span className="text-text-muted text-xs font-bold uppercase">Total de ventas</span>
              <span className="text-emerald-600 dark:text-emerald-400 text-xl font-bold">{formatCOP(monthPayments.reduce((s, p) => s + Number(p.amount), 0))}</span>
            </div>
            <p className="text-text-muted text-[11px] -mt-2">{monthPayments.length} cobro{monthPayments.length !== 1 ? 's' : ''} en el mes</p>

            <div className="border-t border-border pt-3 flex flex-col gap-2">
              {methodTotals.cash > 0 && (
                <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5" /> Efectivo</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(methodTotals.cash)}</span></div>
              )}
              {methodTotals.card > 0 && (
                <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Tarjeta</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(methodTotals.card)}</span></div>
              )}
              {methodTotals.transfer > 0 && (
                <div className="flex justify-between text-sm"><span className="text-text-muted flex items-center gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5" /> Transferencia</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(methodTotals.transfer)}</span></div>
              )}
              {methodTotals.other > 0 && (
                <div className="flex justify-between text-sm"><span className="text-text-muted">Otros</span><span className="text-text-primary font-medium">{formatCOP(methodTotals.other)}</span></div>
              )}
            </div>

            {monthlyReconciliation.reconciledCount > 0 && (
              <div className="border-t border-border pt-3 flex flex-col gap-2.5">
                <h3 className="text-text-primary text-xs font-bold uppercase tracking-wide flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> Reconciliación de caja {loadingMonthlyExpenses && <span className="text-text-muted font-normal">(cargando...)</span>}
                </h3>
                {monthlyReconciliation.totalOpening > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-text-muted">Fondo inicial total</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(monthlyReconciliation.totalOpening)}</span></div>
                )}
                {monthlyReconciliation.totalExpenses > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-text-muted">Gastos de caja</span><span className="text-amber-600 dark:text-amber-400 font-medium tabular-nums">-{formatCOP(monthlyReconciliation.totalExpenses)}</span></div>
                )}
                <div className="flex justify-between text-sm"><span className="text-text-muted">Efectivo esperado</span><span className="text-text-primary font-bold tabular-nums">{formatCOP(monthlyReconciliation.totalExpected)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-text-muted">Efectivo contado</span><span className="text-text-primary font-medium tabular-nums">{formatCOP(monthlyReconciliation.totalCounted)}</span></div>
                <div className={`flex items-center justify-between text-xs font-semibold px-3 py-2 rounded-lg border ${
                  Math.abs(monthlyDifference) < 0.01
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                    : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400'
                }`}>
                  <span className="flex items-center gap-1.5">
                    {Math.abs(monthlyDifference) < 0.01 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {Math.abs(monthlyDifference) < 0.01 ? 'Caja exacta' : monthlyDifference > 0 ? 'Sobrante' : 'Faltante'}
                  </span>
                  <span>{formatCOP(Math.abs(monthlyDifference))}</span>
                </div>
                <p className="text-text-muted text-[11px]">{monthlyReconciliation.reconciledCount} cierre{monthlyReconciliation.reconciledCount !== 1 ? 's' : ''} recibido{monthlyReconciliation.reconciledCount !== 1 ? 's' : ''} en el mes</p>
              </div>
            )}

            {monthlyTypeBreakdown.length > 0 && (
              <div className="border-t border-border pt-3 flex flex-col gap-1.5">
                <h3 className="text-text-primary text-xs font-bold uppercase tracking-wide">Ventas por tipo</h3>
                {monthlyTypeBreakdown.map(t => (
                  <div key={t.name} className="flex justify-between text-sm">
                    <span className="text-text-muted">{t.name} <span className="text-text-muted/60">×{t.count}</span></span>
                    <span className="text-text-primary font-medium tabular-nums">{formatCOP(t.total)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-border pt-3 flex flex-col gap-1.5">
              <h3 className="text-text-primary text-xs font-bold uppercase tracking-wide">Cierres del mes</h3>
              {monthClosings.length === 0 ? (
                <p className="text-text-muted text-xs">Sin cierres registrados en este mes.</p>
              ) : (
                monthClosings.map(c => (
                  <div key={c.id} className={`flex justify-between text-xs ${c.status === 'voided' ? 'opacity-50' : ''}`}>
                    <span className="text-text-muted">#{c.closing_number} · {formatDate(c.date_from)} → {formatDate(c.date_to)} · {c.responsible}</span>
                    <span className="text-text-primary font-medium tabular-nums">{formatCOP(Number(c.grand_total))}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={handleDownloadMonthlyPdf} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white text-sm font-semibold rounded-md hover:bg-accent/90 transition-colors cursor-pointer">
              <FileDown className="w-4 h-4" /> Descargar PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!pendingDelete} onOpenChange={() => setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Eliminar cierre?</AlertDialogTitle>
          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm void */}
      <AlertDialog open={!!pendingVoid} onOpenChange={o => { if (!o) { setPendingVoid(null); setVoidReason('') } }}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Anular este cierre?</AlertDialogTitle>
          <AlertDialogDescription>
            El cierre queda marcado como anulado y deja de contar en los totales recibidos, pero se conserva como registro de auditoría.
          </AlertDialogDescription>
          <div className="mt-2">
            <label className={labelCls}>Motivo (opcional)</label>
            <textarea
              className={inputCls} rows={2} maxLength={300}
              value={voidReason}
              onChange={e => setVoidReason(e.target.value)}
              placeholder="Ej: cierre duplicado, error de carga..."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleVoid} disabled={voiding}>
              {voiding ? 'Anulando...' : 'Anular'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
